import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, CreditCard } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, getCartTotal, getCartCount, getDiscount, getDeliveryFee, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
  });

  const subtotal = getCartTotal();
  const discount = getDiscount();
  const discountAmount = discount.amount;
  const afterDiscount = subtotal - discountAmount;
  const deliveryFee = getDeliveryFee();
  const total = afterDiscount + deliveryFee;
  const totalQuantity = getCartCount();

  useEffect(() => {
    // Check if payment was cancelled
    if (searchParams.get('payment_cancelled')) {
      toast.error('Payment was cancelled. Please try again.');
    }
  }, [searchParams]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        ...formData,
        items: cart.map((item) => ({
          menu_item_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
        })),
        subtotal,
        discount_percentage: discount.percentage,
        discount_amount: discountAmount,
        delivery_fee: deliveryFee,
        total,
      };

      // Get origin URL for success/cancel redirects
      const originUrl = window.location.origin;

      // Create Stripe checkout session
      const response = await axios.post(`${API}/payments/create-checkout-session`, {
        order_data: orderData,
        origin_url: originUrl
      });

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f9f7ed' }}>
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#a80707' }}>Your cart is empty</h2>
          <Button onClick={() => navigate('/')} className="text-white" style={{ backgroundColor: '#a80707' }} data-testid="back-to-menu-btn">
            Back to Menu
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" style={{ backgroundColor: '#f9f7ed' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6"
          style={{ color: '#a80707' }}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Menu
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: '#a80707' }}>Delivery Information</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="customer_name">Full Name *</Label>
                <Input
                  id="customer_name"
                  name="customer_name"
                  type="text"
                  required
                  value={formData.customer_name}
                  onChange={handleInputChange}
                  className="mt-1 border-2"
                  style={{ borderColor: '#a80707' }}
                  data-testid="customer-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="mt-1 border-2"
                  style={{ borderColor: '#a80707' }}
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="mt-1 border-2"
                  style={{ borderColor: '#a80707' }}
                  data-testid="phone-input"
                />
              </div>

              <div>
                <Label htmlFor="address">Street Address *</Label>
                <Input
                  id="address"
                  name="address"
                  type="text"
                  required
                  value={formData.address}
                  onChange={handleInputChange}
                  className="mt-1 border-2"
                  style={{ borderColor: '#a80707' }}
                  data-testid="address-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    type="text"
                    required
                    value={formData.city}
                    onChange={handleInputChange}
                    className="mt-1 border-2"
                    style={{ borderColor: '#a80707' }}
                    data-testid="city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="postal_code">Postal Code *</Label>
                  <Input
                    id="postal_code"
                    name="postal_code"
                    type="text"
                    required
                    value={formData.postal_code}
                    onChange={handleInputChange}
                    className="mt-1 border-2"
                    style={{ borderColor: '#a80707' }}
                    data-testid="postal-code-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full text-white font-semibold py-6 text-lg flex items-center justify-center gap-2"
                style={{ backgroundColor: '#a80707' }}
                data-testid="proceed-to-payment-btn"
              >
                {loading ? (
                  'Processing...'
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    Proceed to Payment
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: '#a80707' }}>Order Summary</h2>
            <div className="space-y-4 mb-6">
              {cart.map((item) => (
                <div key={item.id} className="flex gap-4 pb-4 border-b border-gray-200" data-testid={`summary-item-${item.id}`}>
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{item.name}</h3>
                    <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                    <p className="text-sm font-semibold mt-1" style={{ color: '#a80707' }}>
                      CA${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-3 pt-4 border-t border-gray-200">
              {/* Delivery Fee Notice */}
              {deliveryFee > 0 && (
                <div className="rounded-lg p-3 mb-3 text-sm" style={{ backgroundColor: '#fff3cd' }}>
                  <p className="font-semibold" style={{ color: '#856404' }}>
                    🚚 Add {10 - totalQuantity} more plate{10 - totalQuantity > 1 ? 's' : ''} to get FREE delivery!
                  </p>
                </div>
              )}
              
              {deliveryFee === 0 && totalQuantity >= 10 && (
                <div className="rounded-lg p-3 mb-3 text-sm" style={{ backgroundColor: '#d4edda' }}>
                  <p className="font-semibold" style={{ color: '#155724' }}>
                    🎉 FREE delivery on your order!
                  </p>
                </div>
              )}

              {discount.percentage > 0 && (
                <div className="rounded-lg p-3 mb-3 text-sm" style={{ backgroundColor: '#f9f7ed' }}>
                  <p className="font-semibold" style={{ color: '#a80707' }}>
                    🎉 {discount.percentage}% discount applied! You're saving CA${discountAmount.toFixed(2)}
                  </p>
                </div>
              )}
              
              <div className="flex justify-between text-gray-600">
                <span>Subtotal ({totalQuantity} plates)</span>
                <span data-testid="summary-subtotal">CA${subtotal.toFixed(2)}</span>
              </div>
              
              {discount.percentage > 0 && (
                <div className="flex justify-between font-semibold" style={{ color: '#a80707' }}>
                  <span>Discount ({discount.percentage}%)</span>
                  <span data-testid="summary-discount">-CA${discountAmount.toFixed(2)}</span>
                </div>
              )}
              
              {deliveryFee > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Delivery Fee</span>
                  <span data-testid="summary-delivery">CA${deliveryFee.toFixed(2)}</span>
                </div>
              )}
              
              <div className="flex justify-between text-xl font-bold pt-3 border-t border-gray-200" style={{ color: '#a80707' }}>
                <span>Total</span>
                <span data-testid="summary-total">CA${total.toFixed(2)}</span>
              </div>
            </div>

            <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: '#f9f7ed' }}>
              <p className="text-sm text-gray-600 flex items-center gap-2">
                <CreditCard className="h-4 w-4" style={{ color: '#a80707' }} />
                Secure payment powered by Stripe
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;